import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectTeacherCliPreCasesDetail,
  tryGetTeacherCliprecasesSubdetail,
} from "../../../store/slices/teacher-payments";
import {
  Grid,
  Button,
  Drawer,
  Typography,
  IconButton,
  Chip,
  Box,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  Divider,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import CustomColumnMenu from "../../layout/grid-column-menu";
import ModalMessages from "../../layout/modal-messages";
import TeachersPaymentsDetailSkeleton from "../skeleton/detailDrawerSkeleton";
import { NumericFormat } from "react-number-format";
import { DataGrid } from "@mui/x-data-grid";
import { v4 as uuidv4 } from "uuid";
import Spinner from "../../layout/spinner";

const DetailDrawer = ({
  openDetailDrawer,
  setOpenDetailDrawer,
  businessId,
  cliPrecasesid,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [rows, setRows] = useState([]);

  const precasesSubdetail = useSelector(selectTeacherCliPreCasesDetail);

  const fetchData = async () => {
    const action = await dispatch(
      tryGetTeacherCliprecasesSubdetail({
        teacherId: businessId,
        cliPrecasesid,
      })
    );

    if (action.payload.data.isValid) {
      setRows(
        action.payload.data.data.map((x) => {
          return {
            clientId: x.clientId,
            processType: x.processType ?? "",
            notes: x.notes ?? "",
            totalHours: x.totalHours ?? "",
            amount: x.amount ?? "",
          };
        })
      );
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeDataGrid = () => {
    return {
      ...rows,
      columns: {
        ...columns,
        columnVisibilityModel: {
          clientId: false,
        },
      },
      pagination: { paginationModel: { pageSize: 6 } },
    };
  };

  const columns = [
    { field: "clientId", headerName: "clientId", filterable: false },
    {
      field: "processType",
      headerAlign: "center",
      align: "center",
      flex: 0.3,
      headerName: "TIPO",
      headerClassName: "headRow",
      width: 260,
    },
    {
      field: "notes",
      headerAlign: "center",
      align: "center",
      flex: 0.3,
      headerName: "DETALLE",
      headerClassName: "headRow",
      width: 130,
    },
    {
      field: "totalHours",
      headerAlign: "center",
      align: "center",
      flex: 0.3,
      headerName: "HORAS",
      headerClassName: "headRow",
      width: 130,
      renderCell: (params) => {
        return params.value !== "" && params.value !== null ? (
          <Chip label={`${params.value} hs.`} size="small" />
        ) : null;
      },
    },
    {
      field: "amount",
      headerAlign: "center",
      align: "center",
      flex: 0.3,
      headerName: "MONTO",
      headerClassName: "headRow",
      width: 130,
      renderCell: (params) => (
        <NumericFormat
          key={`${params.field}_${params.id}`}
          value={params.value ?? 0}
          decimalScale={2}
          thousandSeparator="."
          displayType="text"
          decimalSeparator=","
          prefix="$"
          fixedDecimalScale
        />
      ),
    },
  ];

  return (
    <>
      <Drawer
        anchor={isMobile ? "bottom" : "right"}
        open={openDetailDrawer}
        PaperProps={{
          sx: {
            zIndex: 1203,
            width: { xs: "100vw", sm: 500 },
            maxWidth: "100%",
            maxHeight: { xs: "90vh", sm: "100%" },
            borderRadius: { xs: "12px 12px 0 0", sm: 0 },
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
          },
        }}
        onClose={() => setOpenDetailDrawer(false)}
        sx={{
          zIndex: 1203,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            bgcolor: "#194B6F",
            color: "white",
            position: "relative",
            px: { xs: 2, sm: 3 },
            pt: { xs: 2, sm: 4 },
            pb: { xs: 2, sm: 4 },
          }}
        >
          <IconButton
            sx={{
              position: "absolute",
              top: { xs: 8, sm: 16 },
              right: { xs: 8, sm: 16 },
              color: "white",
            }}
            onClick={() => setOpenDetailDrawer(false)}
          >
            <Close />
          </IconButton>

          <Typography
            align="left"
            sx={{
              fontWeight: 700,
              fontSize: 18,
              pr: 4,
            }}
          >
            Detalle del caso
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            backgroundColor: "white",
            p: { xs: 2, sm: 3 },
          }}
        >
          <Grid
            container
            rowSpacing={3}
            spacing={{ xs: 2, sm: 3 }}
            columnSpacing={{ xs: 1, sm: 2, md: 3 }}
          >
            {precasesSubdetail.isLoading ? (
              <TeachersPaymentsDetailSkeleton />
            ) : (
              <>
                <Grid item xs={12}>
                  {isMobile ? (
                    <List disablePadding>
                      {rows.map((row, index) => (
                        <React.Fragment key={index}>
                          <ListItem
                            disableGutters
                            sx={{
                              py: 1.5,
                              px: 1,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                width: "100%",
                                mb: 0.5,
                              }}
                            >
                              <Typography
                                color="text.primary"
                                sx={{ fontWeight: 600, fontSize: 15 }}
                              >
                                Tipo: {row.processType || "-"}
                              </Typography>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: 15,
                                  color: "text.primary",
                                }}
                              >
                                <NumericFormat
                                  value={row.amount ?? 0}
                                  decimalScale={2}
                                  thousandSeparator="."
                                  displayType="text"
                                  decimalSeparator=","
                                  prefix="$"
                                  fixedDecimalScale
                                />
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                width: "100%",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                color="text.secondary"
                                sx={{
                                  fontSize: 14,
                                  pr: 2,
                                  wordBreak: "break-word",
                                }}
                              >
                                {row.notes || "Sin detalle"}
                              </Typography>
                              {row.totalHours !== "" && row.totalHours !== null && (
                                <Chip label={`${row.totalHours} hs.`} size="small" />
                              )}
                            </Box>
                          </ListItem>
                          {index < rows.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  ) : (
                    <DataGrid
                      slots={{
                        columnMenu: CustomColumnMenu,
                      }}
                      autoHeight={true}
                      rowSelection={false}
                      getRowId={(row) => uuidv4()}
                      rows={rows}
                      columns={columns}
                      rowsPerPageOptions={[5]}
                      initialState={initializeDataGrid()}
                    />
                  )}
                </Grid>
                <Grid item xs={12} sx={{ textAlign: "center" }}>
                  {isMobile ? (
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => setOpenDetailDrawer(false)}
                      sx={{ mt: 1, mb: 2 }}
                    >
                      {precasesSubdetail.isLoading && (
                        <Spinner color="primary" />
                      )}
                      Aceptar
                    </Button>
                  ) : (
                    <Box sx={{ mt: 2, mb: 2, display: "inline-block" }}>
                      <Button
                        variant="contained"
                        onClick={() => setOpenDetailDrawer(false)}
                        sx={{ width: 100, marginRight: 2 }}
                      >
                        {precasesSubdetail.isLoading && (
                          <Spinner color="primary" />
                        )}
                        Aceptar
                      </Button>
                    </Box>
                  )}
                </Grid>
              </>
            )}
          </Grid>
        </Box>
        {precasesSubdetail.errors && precasesSubdetail.errors.length > 0 && (
          <ModalMessages errors={precasesSubdetail.errors} />
        )}
      </Drawer>
    </>
  );
};

export default DetailDrawer;